import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import store from '@/store'
import { hasPermission } from '@/composables/usePermission'
import useAuditLog from '@/composables/useAuditLog'

export default function useCompanyEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItem, updateItem } = useLambda()
  const { addAuditEntry } = useAuditLog()
  const userData = store.state?.userStore?.userData
  const company = ref({})
  const originalData = ref(null)
  const isSubmitting = ref(false)
  const query = { _id: oid(route.value.params.id), client_id: oid(userData.client.$oid) }
  const collection = 'company'

  const show = async () => {
    isSubmitting.value = true

    try {
      const item = await getItem({ collection, query })

      // Inicializar history si no existe (documentos legacy)
      if (!item.history) {
        item.history = []
      }

      // Convert ObjectId arrays to string arrays for v-select
      if (item.economicCrimesLawMainInvestigators) {
        item.economicCrimesLawMainInvestigators = item.economicCrimesLawMainInvestigators.map(id => id.toString())
      }
      if (item.economicCrimesLawSecondaryInvestigators) {
        item.economicCrimesLawSecondaryInvestigators = item.economicCrimesLawSecondaryInvestigators.map(id => id.toString())
      }
      if (item.karinLawMainInvestigators) {
        item.karinLawMainInvestigators = item.karinLawMainInvestigators.map(id => id.toString())
      }
      if (item.karinLawSecondaryInvestigators) {
        item.karinLawSecondaryInvestigators = item.karinLawSecondaryInvestigators.map(id => id.toString())
      }
      if (item.ethicalSituationsMainInvestigators) {
        item.ethicalSituationsMainInvestigators = item.ethicalSituationsMainInvestigators.map(id => id.toString())
      }
      if (item.ethicalSituationsSecondaryInvestigators) {
        item.ethicalSituationsSecondaryInvestigators = item.ethicalSituationsSecondaryInvestigators.map(id => id.toString())
      }

      company.value = item
      originalData.value = JSON.parse(JSON.stringify(item))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.company_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async (data) => {
    if (!hasPermission('organization-companies', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    isSubmitting.value = true

    try {
      const payload = {
        name: data.name.value,
        economicCrimesLawMainInvestigators: data.economicCrimesLawMainInvestigators?.value?.map(id => oid(id)) || [],
        economicCrimesLawSecondaryInvestigators: data.economicCrimesLawSecondaryInvestigators?.value?.map(id => oid(id)) || [],
        karinLawMainInvestigators: data.karinLawMainInvestigators?.value?.map(id => oid(id)) || [],
        karinLawSecondaryInvestigators: data.karinLawSecondaryInvestigators?.value?.map(id => oid(id)) || [],
        ethicalSituationsMainInvestigators: data.ethicalSituationsMainInvestigators?.value?.map(id => oid(id)) || [],
        ethicalSituationsSecondaryInvestigators: data.ethicalSituationsSecondaryInvestigators?.value?.map(id => oid(id)) || [],
      }

      const action = { $set: payload }

      await updateItem({ collection, query, action })

      await addAuditEntry({
        collection,
        documentId: route.value.params.id,
        originalData: originalData.value,
        updatedData: { ...company.value, ...payload },
      })

      showSuccessMessage(i18n.t('message.company_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.company_update_error'))
    } finally {
      router.push({ name: 'organization-company-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    company,
    isSubmitting,
  }
}
